import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { BD, BDAnalytics } from "../types";
import BaseComboChart from "../components/Base/BaseComboChart";
import { MixedChartData } from "../types/UsersStats";

const toIsoDate = (date: Dayjs | null) =>
  date ? date.format("YYYY-MM-DD") : undefined;

export function BDDetailsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const MAX_RANGE_DAYS = isMobile ? 7 : 14;
  const navigate = useNavigate();
  const { bdId } = useParams();
  const numericId = Number(bdId);

  // ---------- BD settings form state ----------
  const [referralLimit, setReferralLimit] = useState("");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // ---------- Date picker states ----------
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(6, "day"),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  // ---------- Applied dates ----------
  const [appliedStartDate, setAppliedStartDate] = useState<Dayjs | null>(
    dayjs().subtract(6, "day"),
  );
  const [appliedEndDate, setAppliedEndDate] = useState<Dayjs | null>(dayjs());

  // ---------- BD query ----------
  const {
    data: bd,
    isLoading: isBDLoading,
    isError: isBDError,
    error: bdError,
    refetch: refetchBD,
  } = useQuery<BD, Error>({
    queryKey: ["bd", numericId],
    queryFn: async () => {
      const response = await axios.get<BD>(`${ServerRoutes.bd}/${numericId}`);
      return response.data;
    },
    enabled: Number.isFinite(numericId),
  });

  // ---------- Analytics query----------
  const {
    data: analytics,
    isLoading: isAnalyticsLoading,
    isError: isAnalyticsError,
    error: analyticsError,
  } = useQuery<BDAnalytics, Error>({
    queryKey: [
      "bd-analytics",
      numericId,
      appliedStartDate?.format(),
      appliedEndDate?.format(),
    ],
    queryFn: async () => {
      const query = new URLSearchParams();
      const start = toIsoDate(appliedStartDate);
      const end = toIsoDate(appliedEndDate);
      if (start) query.append("startDate", start);
      if (end) query.append("endDate", end);
      const response = await axios.get<BDAnalytics>(
        `${ServerRoutes.bd}/${numericId}/analytics?${query.toString()}`,
      );
      return response.data;
    },
    enabled:
      Number.isFinite(numericId) && !!appliedStartDate && !!appliedEndDate,
  });

  // ---------- Build chart data from real analytics  ----------
  const comboChartData = useMemo((): MixedChartData | null => {
    if (
      !analytics?.dailyBreakdown?.length ||
      !analytics?.weeklyBreakdown?.length
    )
      return null;

    const labels = analytics.dailyBreakdown.map((item) => item.date);
    const dailyNew = analytics.dailyBreakdown.map((item) => item.count);
    const cumulativeTotal = analytics.weeklyBreakdown.map((item) => item.count);

    return {
      title: "Daily New & Total Referrals",
      xAxisLabel: "Date",
      labels,
      bar: {
        title: "Total referrals",
        data: dailyNew,
        yAxisLabel: "Total",
      },
      line: {
        title: "New referrals (daily)",
        data: cumulativeTotal,
        yAxisLabel: "New",
      },
    };
  }, [analytics]);

  // ---------- Date range validation ----------
  const dateRangeValid = useMemo(() => {
    if (!startDate || !endDate) return true;
    const diff = endDate.diff(startDate, "day");
    return diff >= 0 && diff <= MAX_RANGE_DAYS;
  }, [startDate, endDate, MAX_RANGE_DAYS]);

  // ---------- Percentage display ----------
  const percentageUsedDisplay = useMemo(() => {
    const raw = analytics?.percentageUsed ?? 0;
    return (raw * 100).toFixed(2);
  }, [analytics]);

  // ---------- Handlers ----------
  const handleLoadBD = () => {
    if (!bd) return;
    setReferralLimit(bd.referralLimit?.toString() ?? "");
    setNotes(bd.notes ?? "");
    setIsActive(Boolean(bd.isActive));
  };

  const handleSave = async () => {
    if (!Number.isFinite(numericId)) return;
    setIsSubmitting(true);
    try {
      await axios.patch(`${ServerRoutes.bd}/${numericId}`, {
        referralLimit: Number(referralLimit) || 0,
        isActive,
        notes: notes.trim(),
      });
      setSnackbarMessage("BD updated successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      refetchBD();
    } catch (saveError) {
      setSnackbarMessage("Failed to update BD.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!Number.isFinite(numericId)) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${ServerRoutes.bd}/${numericId}`);
      setSnackbarMessage("BD removed successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsDeleteOpen(false);
      navigate("/bd");
    } catch (deleteError) {
      setSnackbarMessage("Failed to remove BD.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleApplyAnalytics = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const handleResetAnalytics = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
  };

  // ---------- Status chip ----------
  const statusChip = bd?.isActive ? (
    <Chip label="Active" color="success" size="small" />
  ) : (
    <Chip label="Inactive" color="default" size="small" />
  );

  // ---------- JSX ----------
  return (
    <Card sx={{ p: isMobile ? 2 : 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            BD Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Number.isFinite(numericId) ? `ID: ${numericId}` : "Invalid BD ID"}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate("/bd")}>
          Back to BD List
        </Button>
      </Box>

      {/* BD Info Paper */}
      {isBDError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(bdError as Error).message}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        {isBDLoading ? (
          <Box p={2}>
            {[...Array(3)].map((_, index) => (
              <Skeleton key={index} variant="text" height={32} />
            ))}
          </Box>
        ) : bd ? (
          <Stack spacing={2}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <Typography variant="h6">
                {bd.user?.username || bd.user?.firstName || "BD User"}
              </Typography>
              {statusChip}
            </Box>
            <Stack direction={isMobile ? "column" : "row"} spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Telegram ID
                </Typography>
                <Typography>{bd.user?.telegramId || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography>{bd.user?.email || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Invite Code
                </Typography>
                <Typography>{bd.user?.inviteCode || "—"}</Typography>
              </Box>
            </Stack>
            <Stack direction={isMobile ? "column" : "row"} spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Referral Limit
                </Typography>
                <Typography>{bd.referralLimit}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Current Referrals
                </Typography>
                <Typography>{bd.currentReferrals}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created By
                </Typography>
                <Typography>{bd.createdBy?.username || "—"}</Typography>
              </Box>
            </Stack>
          </Stack>
        ) : (
          <Typography>No BD data found.</Typography>
        )}
      </Paper>

      {/* Update BD Settings */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <Typography variant="h6" mb={2}>
          Update BD Settings
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Referral Limit"
            value={referralLimit}
            onChange={(event) => setReferralLimit(event.target.value)}
            type="number"
            size="small"
          />
          <FormControl size="small">
            <InputLabel id="bd-active-update-label">Status</InputLabel>
            <Select
              labelId="bd-active-update-label"
              label="Status"
              value={isActive ? "true" : "false"}
              onChange={(event) => setIsActive(event.target.value === "true")}
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            rows={3}
          />
        </Box>
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={handleLoadBD}
            disabled={isBDLoading || !bd}
          >
            Load Current Data
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSubmitting || !Number.isFinite(numericId)}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setIsDeleteOpen(true)}
          >
            Remove BD
          </Button>
        </Box>
      </Paper>

      {/* Analytics Section */}
      <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
        <Typography variant="h6" mb={2}>
          Analytics
        </Typography>
        <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={2}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button
            variant="contained"
            onClick={handleApplyAnalytics}
            disabled={!dateRangeValid}
          >
            Apply
          </Button>
          {!dateRangeValid && (
            <Typography
              variant="caption"
              color="error"
              sx={{ ml: 1, alignSelf: "center" }}
            >
              Max {MAX_RANGE_DAYS} days allowed.
            </Typography>
          )}
          <Button variant="text" onClick={handleResetAnalytics}>
            Clear Dates
          </Button>
        </Stack>

        {isAnalyticsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(analyticsError as Error).message}
          </Alert>
        ) : null}

        {isAnalyticsLoading ? (
          <Box p={2}>
            {[...Array(2)].map((_, index) => (
              <Skeleton key={index} variant="text" height={28} />
            ))}
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          </Box>
        ) : analytics ? (
          <Stack spacing={2}>
            <Stack direction={isMobile ? "column" : "row"} spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Referrals
                </Typography>
                <Typography>{analytics.totalReferrals}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Referrals In Range
                </Typography>
                <Typography>{analytics.referralsInRange}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Percentage Used
                </Typography>
                <Typography>{percentageUsedDisplay}%</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Active Referrals
                </Typography>
                <Typography>{analytics.activeReferrals}</Typography>
              </Box>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                overflowX: "auto",
              }}
            >
              <Typography variant="subtitle1" mb={1}>
                Daily Referrals
              </Typography>
              {/* Always render sample chart for now */}
              <BaseComboChart data={comboChartData!} />
            </Paper>
          </Stack>
        ) : (
          <Typography color="text.secondary">No analytics data.</Typography>
        )}
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onClose={() => setIsDeleteOpen(false)}>
        <DialogTitle>Remove BD</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove BD status for this user?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}
